import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { User, Mail, Camera, Trash2, Save, Loader2, ArrowLeft, Shield, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [isPublicStash, setIsPublicStash] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        // /api/auth/me returns User model directly (flat), not wrapped in {user: ...}
        const userData = response.data;
        if (userData?.user_id) {
          setUser(userData);
          setName(userData.name || '');
          setEmail(userData.email || '');
          setAvatar(userData.picture || '');
          setIsPublicStash(userData.is_public_stash || false);
        } else {
          navigate('/login?returnTo=/profile');
        }
      } catch (error) {
        navigate('/login?returnTo=/profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.put(`${API}/users/profile`, {
        name,
        picture: avatar,
        is_public_stash: isPublicStash
      }, { withCredentials: true });
      
      setUser(response.data);
      toast.success(t('profile.saved', 'Profile saved successfully!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.saveError', 'Failed to save profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('profile.invalidImageType', 'Please upload a JPEG, PNG, or WebP image'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('profile.imageTooLarge', 'Image must be less than 2MB'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (response.data?.url) {
        setAvatar(response.data.url);
        toast.success(t('profile.avatarUploaded', 'Avatar uploaded!'));
      }
    } catch (error) {
      toast.error(t('profile.uploadError', 'Failed to upload avatar'));
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    
    try {
      await axios.delete(`${API}/users/account`, { withCredentials: true });
      toast.success(t('profile.accountDeleted', 'Your account has been deleted'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.deleteError', 'Failed to delete account'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('profile.backToDashboard', 'Back to Dashboard')}
          </Link>
          <h1 className="text-3xl font-heading font-bold">
            {t('profile.title', 'Profile Settings')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('profile.subtitle', 'Manage your account settings and preferences')}
          </p>
        </div>

        <form onSubmit={handleSaveProfile} className="space-y-8">
          {/* Profile Info Section */}
          <div className="bg-white rounded-3xl border border-stone-100 p-6 shadow-sm">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('profile.personalInfo', 'Personal Information')}
            </h2>
            
            {/* Avatar */}
            <div className="flex items-center gap-6 mb-6">
              <div className="relative">
                <div className="h-24 w-24 rounded-full bg-stone-100 flex items-center justify-center overflow-hidden border-2 border-stone-200">
                  {avatar ? (
                    <img src={avatar} alt={name} className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-12 w-12 text-stone-400" />
                  )}
                </div>
                <label 
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 h-8 w-8 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </label>
                <input 
                  type="file" 
                  id="avatar-upload" 
                  className="hidden" 
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                />
              </div>
              <div>
                <p className="font-medium">{t('profile.avatarLabel', 'Profile Photo')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('profile.avatarHint', 'JPEG, PNG or WebP. Max 2MB.')}
                </p>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="name">{t('profile.nameLabel', 'Display Name')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('profile.namePlaceholder', 'Your name')}
                className="rounded-xl"
              />
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('profile.emailLabel', 'Email Address')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  value={email}
                  disabled
                  className="pl-10 rounded-xl bg-stone-50"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('profile.emailHint', 'Email cannot be changed. Sign in with a different account to use a different email.')}
              </p>
            </div>
          </div>

          {/* Privacy Section */}
          <div className="bg-white rounded-3xl border border-stone-100 p-6 shadow-sm">
            <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {t('profile.privacy', 'Privacy Settings')}
            </h2>
            
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                {isPublicStash ? (
                  <Eye className="h-5 w-5 text-green-600" />
                ) : (
                  <EyeOff className="h-5 w-5 text-stone-400" />
                )}
                <div>
                  <p className="font-medium">{t('profile.publicStash', 'Public Yarn Stash')}</p>
                  <p className="text-sm text-muted-foreground">
                    {isPublicStash 
                      ? t('profile.publicStashOn', 'Your yarn collection is visible to everyone')
                      : t('profile.publicStashOff', 'Only you can see your yarn collection')
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={isPublicStash}
                onCheckedChange={setIsPublicStash}
              />
            </div>
          </div>

          {/* Save Button */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full rounded-full"
            size="lg"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('profile.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('profile.saveChanges', 'Save Changes')}
              </>
            )}
          </Button>
        </form>

        {/* Danger Zone */}
        <div className="mt-8 bg-red-50 rounded-3xl border border-red-200 p-6">
          <h2 className="text-lg font-heading font-semibold mb-2 text-red-800 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {t('profile.dangerZone', 'Danger Zone')}
          </h2>
          <p className="text-sm text-red-700 mb-4">
            {t('profile.deleteWarning', 'Once you delete your account, there is no going back. All your data will be permanently removed.')}
          </p>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="rounded-full">
                <Trash2 className="h-4 w-4 mr-2" />
                {t('profile.deleteAccount', 'Delete Account')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('profile.deleteConfirmTitle', 'Are you absolutely sure?')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('profile.deleteConfirmDescription', 'This action cannot be undone. This will permanently delete your account, yarn collection, projects, and all associated data.')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">
                  {t('common.cancel', 'Cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="rounded-full bg-red-600 hover:bg-red-700"
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {t('profile.confirmDelete', 'Yes, delete my account')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>
    </div>
  );
}
